import {
  type BaseCurrentCartServiceProps,
  AddToCurrentCartV1Options,
  ApplicationError,
  Cart,
  CartV1,
  CreateCheckoutFromCurrentCartOptions,
  EstimateCurrentCartTotals,
  EstimateCurrentCartTotalsOptions,
  ILeanGQLCartModel,
} from './types';
import {
  AddToCartActionOption,
  APP_DEFINITION_ID,
  CartEvents,
} from '@wix/wixstores-client-core';
import { MiniCartEvents, SecondaryEvents, SPECS } from '../../constants';
import { createClient } from '@wix/sdk/client';
import {
  currentCart as currentCartV1,
  currentCartV2 as currentCart,
} from '@wix/ecom';
import { getLeanGQLCartFromCart } from './getLeanGQLCartFromCart';
import { getLeanGQLCartFromCartV1 } from './getLeanGQLCartFromCartV1';
import { AnalyticsServiceV1 } from '../AnalyticsServiceV1';
import { BIServiceV1 } from '../BIServiceV1';
import { deserialize, serialize } from '../../utils/serialize';
import { getSDKAuthAndHost } from '../../utils/getSDKAuthAndHost';
import { ViewerStorage } from '../viewer-storage/ViewerStorage';
import { getCurrencyHeader } from '../../utils/headers';
import { BaseCurrentCartService } from './BaseCurrentCartService';

const CACHE_KEY = 'CURRENT_CART';
const CACHE_KEY_V1 = 'CURRENT_CART_V1';
const origin = 'current-cart-service';

export class CurrentCartService extends BaseCurrentCartService {
  private readonly currentCartApi;
  private readonly currentCartApiV1;
  private cartV1Promise?: Promise<CartV1>;
  private cartPromise?: Promise<Cart>;
  private readonly biService: BIServiceV1;
  private readonly analyticsService: AnalyticsServiceV1;
  private shouldRefetchOnNextGet: boolean = false;
  private readonly viewerStorageV1!: ViewerStorage<{
    cart: CartV1;
    currency: string;
    multiLang: string;
  }>;
  private readonly viewerStorage!: ViewerStorage<{
    cart: Cart;
    currency: string;
    multiLang: string;
  }>;
  private stopListeningToInstanceChanged = false;

  constructor(props: BaseCurrentCartServiceProps) {
    super(props);

    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      this.viewerStorage = new ViewerStorage({
        cacheKey: CACHE_KEY,
        storage: this.apis.storage,
        siteStore: this.siteStore,
      });
    } else {
      this.viewerStorageV1 = new ViewerStorage({
        cacheKey: CACHE_KEY_V1,
        storage: this.apis.storage,
        siteStore: this.siteStore,
      });
    }
    this.biService = new BIServiceV1({ siteStore: this.siteStore, origin });
    this.analyticsService = new AnalyticsServiceV1({
      siteStore: this.siteStore,
    });

    const wixClient = createClient({
      ...getSDKAuthAndHost(this.siteStore),
      modules: {
        currentCart: {
          getCurrentCart: currentCart.getCurrentCart,
          removeLineItemsFromCurrentCart:
            currentCart.removeLineItemsFromCurrentCart,
          addCouponToCurrentCart: currentCart.addCouponToCurrentCart,
          removeCouponFromCurrentCart: currentCart.removeCouponFromCurrentCart,
          addLineItemsToCurrentCart: currentCart.addLineItemsToCurrentCart,
          updateLineItemsInCurrentCart:
            currentCart.updateLineItemsInCurrentCart,
          calculateCurrentCart: currentCart.calculateCurrentCart,
          estimateCurrentCart: currentCart.estimateCurrentCart,
          setDeliveryMethodForCurrentCart:
            currentCart.setDeliveryMethodForCurrentCart,
          removeDeliveryMethodFromCurrentCart:
            currentCart.removeDeliveryMethodFromCurrentCart,
          updateCurrentCart: currentCart.updateCurrentCart,
          removeGiftCardFromCurrentCart:
            currentCart.removeGiftCardFromCurrentCart,
          addGiftCardToCurrentCart: currentCart.addGiftCardToCurrentCart,
          refreshCurrentCart: currentCart.refreshCurrentCart,
          updateFormSubmissionsInCurrentCart:
            currentCart.updateFormSubmissionsInCurrentCart,
        },
        currentCartV1: {
          getCurrentCart: currentCartV1.getCurrentCart,
          estimateCurrentCartTotals: currentCartV1.estimateCurrentCartTotals,
          addToCurrentCart: currentCartV1.addToCurrentCart,
          updateCurrentCart: currentCartV1.updateCurrentCart,
          updateCurrentCartLineItemQuantity:
            currentCartV1.updateCurrentCartLineItemQuantity,
          removeCouponFromCurrentCart:
            currentCartV1.removeCouponFromCurrentCart,
          removeLineItemsFromCurrentCart:
            currentCartV1.removeLineItemsFromCurrentCart,
          createCheckoutFromCurrentCart:
            currentCartV1.createCheckoutFromCurrentCart,
        },
      },
    });
    this.currentCartApiV1 = wixClient.currentCartV1;
    this.currentCartApi = wixClient.currentCart;
  }
  public init() {
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.getCurrentCart().catch(() => {
        //
      });
    } else {
      void this.getCurrentCartV1().catch(() => {
        //
      });
    }

    this.listenToCurrentCartEvents();
    this.listenToSecondaryCurrentCartServiceRequests();
    this.listenToPageRestore();
  }

  private async getCurrentCartInternal() {
    if (!this.cartPromise || this.shouldRefetchOnNextGet) {
      this.cartPromise = this.getCurrentCartFromCacheOrServer();
      this.cartPromise.catch(() => {
        this.shouldRefetchOnNextGet = true;
      });
      if (this.shouldRefetchOnNextGet) {
        this.shouldRefetchOnNextGet = false;
        const cart = await this.cartPromise;
        await this.notifyChange({ cart });
      }
    }

    return this.cartPromise;
  }

  private async getCurrentCartV1Internal() {
    if (!this.cartV1Promise || this.shouldRefetchOnNextGet) {
      this.cartV1Promise = this.getCurrentCartV1FromCacheOrServer();
      this.cartV1Promise.catch(() => {
        this.shouldRefetchOnNextGet = true;
      });
      if (this.shouldRefetchOnNextGet) {
        this.shouldRefetchOnNextGet = false;
        const cart = await this.cartV1Promise;
        await this.notifyChangeV1({ cart });
      }
    }

    return this.cartV1Promise;
  }

  public async getCurrentCart(): Promise<Cart> {
    if (
      !this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      const { cart } = await this.currentCartApi.getCurrentCart();
      return cart!;
    }
    return this.getCurrentCartInternal();
  }

  public async getCurrentCartV1(): Promise<CartV1> {
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      return this.getCurrentCartV1FromServer();
    }
    return this.getCurrentCartV1Internal();
  }

  public async calculateCurrentCart(
    options?: currentCart.CalculateCurrentCartRequest,
  ): Promise<currentCart.CalculateCurrentCartResponse> {
    return this.currentCartApi.calculateCurrentCart(options);
  }

  public estimateCurrentCart(
    options?: currentCart.EstimateCurrentCartRequest,
  ): Promise<currentCart.EstimateCurrentCartResponse> {
    return this.currentCartApi.estimateCurrentCart(options);
  }

  public estimateCurrentCartTotalsV1(
    options?: EstimateCurrentCartTotalsOptions,
  ): Promise<EstimateCurrentCartTotals> {
    /* istanbul ignore next */
    if (!this.currentCartApiV1) {
      throw new Error('Only supported with sdk');
    }
    return this.currentCartApiV1.estimateCurrentCartTotals(options);
  }

  public invalidateAndReload(): Promise<void> {
    this.shouldRefetchOnNextGet = true;
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      this.invalidateCurrentCartCache();
      void this.getCurrentCartInternal().catch(() => {});
    } else {
      this.invalidateCurrentCartV1Cache();
      this.getCurrentCartV1Internal().catch(
        /* istanbul ignore next */
        () => {},
      );
    }
    return Promise.resolve();
  }

  public async updateLineItemsInCurrentCart(
    options: currentCart.UpdateLineItemsInCurrentCartOptions,
  ) {
    const { cart } = await this.currentCartApi.updateLineItemsInCurrentCart(
      options,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async updateLineItemQuantityV1(
    { lineItemId, quantity }: { lineItemId: string; quantity: number },
    biProps?: { origin?: string },
  ) {
    const { cart } =
      await this.currentCartApiV1.updateCurrentCartLineItemQuantity([
        { _id: lineItemId, quantity },
      ]);
    void this.biService.updateCartItemQuantityV1({
      origin: biProps?.origin,
      cart: cart!,
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(cart!);
  }

  public async createCheckoutV1(options: CreateCheckoutFromCurrentCartOptions) {
    return this.currentCartApiV1.createCheckoutFromCurrentCart(options);
  }

  public async removeGiftCardFromCurrentCart({
    giftCardId,
  }: {
    giftCardId: string;
  }) {
    const { cart } = await this.currentCartApi.removeGiftCardFromCurrentCart(
      giftCardId,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async addGiftCardToCurrentCart({
    giftCardInput,
  }: {
    giftCardInput: currentCart.GiftCardInput;
  }) {
    const { cart } = await this.currentCartApi.addGiftCardToCurrentCart(
      giftCardInput,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeLineItemsFromCurrentCart({
    lineItemIds,
  }: {
    lineItemIds: string[];
  }) {
    const { cart } = await this.currentCartApi.removeLineItemsFromCurrentCart(
      lineItemIds,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeLineItemV1(
    { lineItemId }: { lineItemId: string },
    biProps?: { origin?: string },
  ) {
    const previousCart = await this.getCurrentCartV1();
    const { cart } = await this.currentCartApiV1.removeLineItemsFromCurrentCart(
      [lineItemId],
    );
    void this.biService.removeLineItemV1({
      cart: cart!,
      origin: biProps?.origin,
    });
    this.analyticsService.trackRemoveLineItemV1({
      lineItemId,
      updatedCart: cart!,
      previousCart,
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(cart!);
  }

  public async updateBuyerNoteV1({
    buyerNote,
  }: {
    buyerNote: string;
  }): Promise<void> {
    const updatedCart = await this.currentCartApiV1.updateCurrentCart({
      cartInfo: { buyerNote },
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(updatedCart);
  }

  public async addCouponToCurrentCart({
    coupon,
  }: {
    coupon: currentCart.CouponInput;
  }) {
    const { cart } = await this.currentCartApi.addCouponToCurrentCart(coupon);
    await this.updateCache(cart!);
    return cart!;
  }

  public async applyCouponV1(
    { code }: { code: string },
    biProps?: { origin?: string },
  ) {
    const updatedCart = await this.currentCartApiV1.updateCurrentCart({
      couponCode: code,
    });
    void this.biService.applyCouponV1({
      cart: updatedCart,
      couponCode: code,
      origin: biProps?.origin,
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(updatedCart);
  }

  public async removeCouponFromCurrentCart({
    couponId,
  }: {
    couponId: string;
  }): Promise<Cart> {
    const { cart } = await this.currentCartApi.removeCouponFromCurrentCart(
      couponId,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeCouponV1(biProps?: { origin?: string }): Promise<void> {
    const { appliedDiscounts } = await this.getCurrentCartV1();
    const { cart } = await this.currentCartApiV1.removeCouponFromCurrentCart();
    void this.biService.removeCouponV1({
      origin: biProps?.origin,
      discounts: appliedDiscounts,
      cart: cart!,
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(cart!);
  }

  public async setAddressV1({
    address,
  }: {
    address: { country: string; subdivision?: string; zipCode?: string };
  }) {
    const { country, subdivision, zipCode } = address;
    const cart = await this.currentCartApiV1.updateCurrentCart({
      cartInfo: {
        contactInfo: {
          address: {
            country,
            subdivision: subdivision ?? null,
            postalCode: zipCode ?? null,
            addressLine1: null,
            addressLine2: null,
            city: null,
            streetAddress: null as any,
          },
        },
      },
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    await this.updateCacheV1(cart);
  }

  public async setDeliveryMethodForCurrentCart({
    deliveryMethod,
  }: {
    deliveryMethod: currentCart.DeliveryMethodInput;
  }) {
    const { cart } = await this.currentCartApi.setDeliveryMethodForCurrentCart(
      deliveryMethod,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeDeliveryMethodFromCurrentCart() {
    const { cart } =
      await this.currentCartApi.removeDeliveryMethodFromCurrentCart();
    await this.updateCache(cart!);
    return cart!;
  }

  public async selectShippingOptionV1({
    selectedShippingOption,
    option,
  }: {
    selectedShippingOption?: { optionId: string; shippingRuleId: string };
    option?: { code: string; carrierId: string | null };
  }) {
    const updatedCart: CartV1 = await this.currentCartApiV1.updateCurrentCart({
      cartInfo: {
        selectedShippingOption: {
          code: option?.code ?? selectedShippingOption!.optionId,
          carrierId: option?.carrierId,
        },
      },
    });
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(updatedCart);
  }

  public async addLineItemsToCurrentCart(
    options: currentCart.AddLineItemsToCurrentCartOptions,
  ): Promise<Cart> {
    const { cart } = await this.currentCartApi.addLineItemsToCurrentCart(
      options,
    );
    if (!cart) {
      throw new Error('Failed to Add Line Items To Current Cart');
    }
    await this.updateCache(cart);
    return cart;
  }

  public async addToCurrentCartV1(
    options: AddToCurrentCartV1Options,
  ): Promise<void> {
    const { cart } = await this.currentCartApiV1.addToCurrentCart(options);
    if (!cart) {
      throw new Error('Failed to Add To Current Cart');
    }

    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      void this.invalidateAndReload();
      return;
    }
    return this.updateCacheV1(cart);
  }

  public async updateCurrentCart(cart: Cart): Promise<Cart> {
    const { cart: updatedCart } = await this.currentCartApi.updateCurrentCart(
      cart,
    );
    await this.updateCache(updatedCart!);
    return updatedCart!;
  }

  public async refreshCurrentCart(): Promise<Cart> {
    const { cart } = await this.currentCartApi.refreshCurrentCart();
    await this.updateCache(cart!);
    return cart!;
  }

  private notifyChange({ cart }: { cart: Cart }) {
    this.runCallbacks({ revision: cart.revision });
    this.publishUpdateForSecondaryCurrentCart();
    return this.updateMiniCart(cart);
  }

  public async updateFormSubmissionsInCurrentCart(
    options: currentCart.UpdateFormSubmissionsInCurrentCartOptions,
  ): Promise<Cart> {
    const { cart } =
      await this.currentCartApi.updateFormSubmissionsInCurrentCart(options);
    await this.updateCache(cart!);
    return cart!;
  }

  private notifyChangeV1({ cart }: { cart?: CartV1 }) {
    this.runCallbacks();
    this.publishUpdateForSecondaryCurrentCart();
    return this.updateMiniCartV1(cart);
  }

  private listenToSecondaryCurrentCartServiceRequests() {
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetRequest,
      this.getCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetV1Request,
      this.getCurrentCartV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetCurrentCartIdFromCacheRequest,
      this.getCurrentCartIdFromCache.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RefreshV1Request,
      this.invalidateAndReload.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateLineItemsInCurrentCartRequest,
      this.updateLineItemsInCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateQuantityV1Request,
      this.updateLineItemQuantityV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveLineItemsFromCurrentCartRequest,
      this.removeLineItemsFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveItemV1Request,
      this.removeLineItemV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.OnLoginStartRequest,
      this.onLoginStart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.OnLoginFinishRequest,
      this.onLoginFinish.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateBuyerNoteV1Request,
      this.updateBuyerNoteV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddCouponToCurrentCartRequest,
      this.addCouponToCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.ApplyCouponV1Request,
      this.applyCouponV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveCouponFromCurrentCartRequest,
      this.removeCouponFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveCouponV1Request,
      this.removeCouponV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.SetAddressV1Request,
      this.setAddressV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.SetDeliveryMethodForCurrentCartRequest,
      this.setDeliveryMethodForCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveDeliveryMethodFromCurrentCartRequest,
      this.removeDeliveryMethodFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.SelectShippingOptionV1Request,
      this.selectShippingOptionV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.CalculateCurrentCart,
      this.calculateCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.EstimateCurrentCart,
      this.estimateCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.EstimateCurrentCartTotalsV1,
      this.estimateCurrentCartTotalsV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddLineItemsToCurrentCart,
      this.addLineItemsToCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddToCurrentCartV1,
      this.addToCurrentCartV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.CreateCheckoutV1Request,
      this.createCheckoutV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateCurrentCartRequest,
      this.updateCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RefreshCurrentCartRequest,
      this.refreshCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveGiftCardFromCurrentCartRequest,
      this.removeGiftCardFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddGiftCardToCurrentCartRequest,
      this.addGiftCardToCurrentCart.bind(this),
    );

    this.apis.pubSub.subscribe(
      SecondaryEvents.HEALTH_CHECK,
      () => {
        this.apis.pubSub.publish(SecondaryEvents.ACK, {}, false);
      },
      false,
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateFormSubmissionsInCurrentCartRequest,
      this.updateFormSubmissionsInCurrentCart.bind(this),
    );
  }

  private subscribeToSecondaryEvent<T, S extends unknown[]>(
    event: string,
    performAction: (...payload: S) => Promise<T>,
  ) {
    this.apis.pubSub.subscribe(
      event,
      async ({ data }) => {
        const { key, payload } = deserialize(data);
        let actionResponse;
        let error;
        try {
          actionResponse = await performAction(...((payload || []) as S));
        } catch (e) {
          actionResponse = undefined;
          error = e;
        }
        this.apis.pubSub.publish(
          SecondaryEvents.ActionResponse,
          serialize({ key, payload: actionResponse, error }),
          false,
        );
      },
      false,
    );
  }

  private listenToCurrentCartEvents() {
    let currency = this.siteStore.location.query.currency;
    this.siteStore.location.onChange(() => {
      if (currency !== this.siteStore.location.query.currency) {
        currency = this.siteStore.location.query.currency;

        void this.invalidateAndReload();
      }
    });

    this.siteStore.pubSubManager.subscribe(
      CartEvents.CLEARED,
      (res) => void this.clearCart(res.data.cartId),
    );

    this.siteStore.pubSubManager.subscribe(CartEvents.INVALIDATED, () => {
      if (
        this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
      ) {
        this.invalidateCurrentCartCache();
      } else {
        this.invalidateCurrentCartV1Cache();
      }
    });

    this.siteStore.siteApis.onInstanceChanged(() => {
      if (this.stopListeningToInstanceChanged) {
        return;
      }
      void this.invalidateAndReload();
    }, APP_DEFINITION_ID);

    this.siteStore.pubSubManager.subscribe(
      MiniCartEvents.LoadedWithoutData,
      () => {
        if (
          this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
        ) {
          this.getMiniCartLeanModel().then((cart) => {
            this.siteStore.pubSubManager.publish(
              MiniCartEvents.OnInitialData,
              cart,
            );
          });
        } else {
          this.getMiniCartLeanModelV1().then((cart) => {
            this.siteStore.pubSubManager.publish(
              MiniCartEvents.OnInitialData,
              cart,
            );
          });
        }
      },
    );
  }

  public onLoginStart() {
    this.stopListeningToInstanceChanged = true;
    return Promise.resolve();
  }

  public onLoginFinish() {
    this.stopListeningToInstanceChanged = false;
    return Promise.resolve();
  }

  private async getMiniCartLeanModel(cart?: Cart): Promise<ILeanGQLCartModel> {
    return getLeanGQLCartFromCart({
      currentCartService: this,
      wixApi: this.context.wixApi!,
      platformServices: this.context.platformServices!,
      cart,
    });
  }

  private async getMiniCartLeanModelV1(
    cart?: CartV1,
  ): Promise<ILeanGQLCartModel> {
    return getLeanGQLCartFromCartV1(this, cart);
  }

  private updateCache(cart: Cart) {
    this.cartPromise = Promise.resolve(cart);
    this.shouldRefetchOnNextGet = false;
    this.viewerStorage.set(this.toCacheEntity(cart));
    return this.notifyChange({ cart });
  }

  private updateCacheV1(cart: CartV1) {
    this.cartV1Promise = Promise.resolve(cart);
    this.shouldRefetchOnNextGet = false;
    this.viewerStorageV1.set(this.toCacheEntity(cart));
    return this.notifyChangeV1({ cart });
  }

  private toCacheEntity<T = Cart | CartV1>(cart: T) {
    return {
      cart,
      currency: getCurrencyHeader(this.siteStore.wixCodeApi),
      multiLang: this.siteStore.getMultiLangHeader(),
    };
  }

  private invalidateCurrentCartV1Cache() {
    this.viewerStorageV1.remove();
  }

  private invalidateCurrentCartCache() {
    this.viewerStorage.remove();
  }

  private publishUpdateForSecondaryCurrentCart() {
    this.siteStore.pubSubManager.publish(SecondaryEvents.Updated, {});
  }

  private async updateMiniCart(cart?: Cart) {
    this.siteStore.pubSubManager.publish(CartEvents.CHANGED, {
      ...(await this.getMiniCartLeanModel(cart)),
      extraParams: { origin: 'CurrentCartUpdate' },
      eventOptions: {
        shouldOpenCart: false,
        addToCartActionOption: AddToCartActionOption.MINI_CART,
        originatedInWorker: true,
      },
      reason: 'CurrentCartUpdate',
    });
  }

  private async updateMiniCartV1(cart?: CartV1) {
    this.siteStore.pubSubManager.publish(CartEvents.CHANGED, {
      ...(await this.getMiniCartLeanModelV1(cart)),
      extraParams: { origin: 'CurrentCartUpdate' },
      eventOptions: {
        shouldOpenCart: false,
        addToCartActionOption: AddToCartActionOption.MINI_CART,
        originatedInWorker: true,
      },
      reason: 'CurrentCartUpdate',
    });
  }

  private async clearCart(givenCartId: string) {
    let id: string | undefined | null;
    if (
      this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
    ) {
      id = (await this.getCurrentCart())._id;
      if (givenCartId === id) {
        const cart = { lineItems: [] };
        this.cartPromise = Promise.resolve(cart);
        this.shouldRefetchOnNextGet = false;
        await this.notifyChange({ cart });
        this.invalidateCurrentCartCache();
      }
    } else {
      id = (await this.getCurrentCartV1())._id;

      if (givenCartId === id) {
        const cart = { lineItems: [] };
        this.cartV1Promise = Promise.resolve(cart);
        this.shouldRefetchOnNextGet = false;
        await this.notifyChangeV1({ cart });
        this.invalidateCurrentCartV1Cache();
      }
    }
  }

  public getCurrentCartIdFromCache(): Promise<string | undefined> {
    const cached = this.viewerStorage.get();
    if (cached && this.isCachedCartValid(cached)) {
      return Promise.resolve(cached.cart._id);
    }
    return Promise.resolve(undefined);
  }

  private async getCurrentCartFromCacheOrServer() {
    if (!this.shouldRefetchOnNextGet) {
      const cached = this.viewerStorage.get();
      if (cached && this.isCachedCartValid(cached)) {
        return cached.cart;
      }
    }
    const cart = (await this.getCurrentCartFromServer())!;
    this.viewerStorage.set(this.toCacheEntity(cart));
    return cart;
  }

  private async getCurrentCartV1FromCacheOrServer(): Promise<CartV1> {
    let cart: CartV1;
    if (this.shouldRefetchOnNextGet) {
      cart = await this.getCurrentCartV1FromServer();
    } else {
      const cachedCart = this.viewerStorageV1.get();
      if (cachedCart && this.isCachedCartValid(cachedCart)) {
        return cachedCart.cart;
      }

      cart = await this.getCurrentCartV1FromServer();
    }
    this.viewerStorageV1.set(this.toCacheEntity(cart));

    return cart;
  }

  private isCachedCartValid<T = Cart | CartV1>(cachedCart: {
    cart: T;
    currency: string;
    multiLang: string;
  }) {
    return (
      cachedCart.currency === getCurrencyHeader(this.siteStore.wixCodeApi) &&
      cachedCart.multiLang === this.siteStore.getMultiLangHeader()
    );
  }

  private async getCurrentCartFromServer() {
    try {
      const { cart } = await this.currentCartApi.getCurrentCart();
      return cart;
    } catch (e: unknown) {
      if (
        (e as ApplicationError)?.details?.applicationError?.code ===
        'CURRENT_CART_NOT_FOUND'
      ) {
        return { lineItems: [] };
      }

      throw e;
    }
  }

  private async getCurrentCartV1FromServer() {
    try {
      return await this.currentCartApiV1.getCurrentCart();
    } catch (e: unknown) {
      if (
        (e as ApplicationError)?.details?.applicationError?.code ===
        'OWNED_CART_NOT_FOUND'
      ) {
        return { lineItems: [] };
      }

      throw e;
    }
  }
  private listenToPageRestore() {
    this.context.wixApi!.window.onPageRestore?.(() => {
      this.cartPromise = undefined;
      this.cartV1Promise = undefined;
      this.shouldRefetchOnNextGet = false;

      if (
        this.siteStore.experiments.enabled(SPECS.CallCartV2FromCartComponents)
      ) {
        void this.getCurrentCart().then((cart) => this.notifyChange({ cart }));
      } else {
        void this.getCurrentCartV1().then((cart) =>
          this.notifyChangeV1({ cart }),
        );
      }
    });
  }
}
